/** Shared camera-facing intent.
 *
 * `desiredFacing` (the facing the camera SHOULD be on) used to live as a
 * closure variable inside FieldCommandsHandler, so only the operator-driven
 * `camera.switch` could set it. The auto-restore there snaps the track back to
 * `desired` after a spurious restart — but because the field tech's OWN flip
 * (FieldLayout.handleFlip) couldn't update `desired`, the auto-restore treated
 * that flip as a spurious restart and immediately reverted it ("same camera
 * again and again"). Lifting the intent here lets BOTH the operator switch and
 * the field flip set it, so the auto-restore respects either.
 */
import { create } from "zustand";

import type { FacingMode } from "./cameraCommands";

type CameraIntentState = {
  /** The facing the camera should be on. Set by the operator's camera.switch
   *  AND by the field tech's own flip. Undefined = no preference yet. */
  desired: FacingMode | undefined;
  /** True while a DELIBERATE switch (operator or field) is in flight, so the
   *  auto-restore doesn't fight the in-progress swap. */
  switching: boolean;
  setDesired: (f: FacingMode) => void;
  setSwitching: (b: boolean) => void;
};

export const useCameraIntent = create<CameraIntentState>((set) => ({
  desired: undefined,
  switching: false,
  setDesired: (f) => set({ desired: f }),
  setSwitching: (b) => set({ switching: b }),
}));
